import React, { useState, useEffect, useRef } from "react";
import { Button, Form, Spinner, Alert, Badge } from "react-bootstrap";
import { FaPaperclip, FaTimes, FaFile, FaImage } from "react-icons/fa";
import { chatAPI } from '../../api/apiClient';
import { useTranslation } from 'react-i18next';
import Pusher from 'pusher-js';

// Enable Pusher logging in development
Pusher.logToConsole = process.env.NODE_ENV === 'development';

export default function ChatBox({ customerId, conversationId, title }) {
  const { t } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [customer, setCustomer] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  // Mark messages as read (only for customer messages)
  const markMessagesAsRead = async () => {
    if (!customerId) {
      console.warn('⚠️ No customerId provided for markAsRead');
      return;
    }
    
    try {
      const response = await chatAPI.markAsRead(customerId); // Updated to use customerId
      if (response.success) {
        setMessages(prev => prev.map(msg => 
          msg.sender.id === parseInt(customerId) ? { ...msg, is_read: true } : msg
        ));
        console.log('✅ Customer messages marked as read');
      }
    } catch (err) {
      console.error("❌ Error marking customer messages as read:", err);
    }
  };

  // Initialize Pusher
  useEffect(() => {
    if (!customerId) return;

    const pusher = new Pusher("1a6dafa4504c853b7f61", {
      cluster: "mt1",
      forceTLS: true,
      enabledTransports: ['ws', 'wss'],
    });

    const customerChannel = pusher.subscribe(`chat.customer.${customerId}.admins`);

    const handleMessageSent = (data) => {
      console.log('📨 Pusher message.sent:', data);
      if (data.receiver?.id == customerId || data.sender?.id == customerId) {
        setMessages(prev => {
          if (prev.some(msg => msg.id === data.id)) return prev;
          return [...prev, data];
        });
        scrollToBottom();
        
        if (data.sender?.id == customerId) {
          markMessagesAsRead();
        }
      }
    };

    customerChannel.bind('message.sent', handleMessageSent);

    customerChannel.bind('message.read', (data) => {
      console.log('📖 Pusher message.read:', data);

      setMessages(prev => prev.map(msg =>
        data.messages.some(readMsg => readMsg.id === msg.id)
          ? { ...msg, is_read: true }
          : msg
      ));
    });

    return () => {
      customerChannel.unbind_all();
      pusher.unsubscribe(`chat.customer.${customerId}.admins`);
      pusher.disconnect();
    };
  }, [customerId, conversationId]);

  // Fetch messages and mark as read when chat is opened
  const fetchMessages = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await chatAPI.customerMessages(customerId);
      
      if (response.success) {
        setMessages(response.data.data.messages || []);
        setCustomer(response.data.data.customer);
        
        // Mark messages as read when chat is opened
        await markMessagesAsRead(); // Call markMessagesAsRead immediately
      } else {
        setError(response.message || t('failed_to_fetch_messages'));
      }
    } catch (err) {
      setError(t('failed_to_fetch_messages'));
      console.error("Error fetching messages:", err);
    } finally {
      setLoading(false);
      scrollToBottom();
    }
  };

  // Scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (customerId) {
      fetchMessages();
    }
  }, [customerId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Handle file selection
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setAttachments(prev => [...prev, ...files]);
    }
    // Reset file input
    e.target.value = '';
  };

  // Remove attachment
  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  // Send message with attachments
  const sendMessage = async () => {
    if ((input.trim() === "" && attachments.length === 0) || sending) return;
    
    try {
      setSending(true);
      setError("");

      // Create FormData for file upload
      const formData = new FormData();
      formData.append('receiver_id', customerId);
      formData.append('receiver_type', 'customer');
      formData.append('message', input.trim());
      
      // Add attachments
      attachments.forEach((file, index) => {
        formData.append(`attachments[${index}]`, file);
      });

      const response = await chatAPI.send(formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.success) {
        setInput("");
        setAttachments([]);
      } else {
        setError(response.message || t('failed_to_send_message'));
      }
    } catch (err) {
      setError(t('failed_to_send_message'));
      console.error("Error sending message:", err);
    } finally {
      setSending(false);
    }
  };

  // Render attachment preview
  const renderAttachmentPreview = () => {
    if (attachments.length === 0) return null;

    return (
      <div className="attachments-preview mb-2 p-2 border rounded bg-light">
        <small className="text-muted d-block mb-1">{t('attachments')}:</small>
        {attachments.map((file, index) => (
          <div key={index} className="d-flex align-items-center justify-content-between mb-1">
            <div className="d-flex align-items-center flex-grow-1 me-2">
              {file.type.startsWith('image/') ? (
                <FaImage className="text-primary me-2" />
              ) : (
                <FaFile className="text-secondary me-2" />
              )}
              <small className="text-truncate">{file.name}</small>
            </div>
            <Button
              variant="link"
              className="text-danger p-0"
              onClick={() => removeAttachment(index)}
              style={{ minWidth: 'auto' }}
            >
              <FaTimes size={12} />
            </Button>
          </div>
        ))}
      </div>
    );
  };

  // Render message attachments
  const renderMessageAttachments = (attachments) => {
    if (!attachments || attachments.length === 0) return null;

    return (
      <div className="message-attachments mt-2">
        {attachments.map((attachment, index) => (
          <div key={attachment.id || index} className="mb-2">
            {attachment.is_image ? (
              <div className="image-attachment">
                <a href={attachment.file_url} target="_blank" rel="noopener noreferrer">
                  <img 
                    src={attachment.file_url} 
                    alt={attachment.filename}
                    style={{ 
                      maxWidth: '200px', 
                      maxHeight: '150px', 
                      borderRadius: '8px',
                      cursor: 'pointer'
                    }}
                    className="img-thumbnail"
                  />
                </a>
                <small className="d-block text-light mt-1">{attachment.filename}</small>
              </div>
            ) : (
              <div className="file-attachment">
                <a 
                  href={attachment.file_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="d-flex align-items-center border rounded p-2 text-decoration-none bg-white"
                >
                  <FaFile className="me-2 text-primary" />
                  <div className="flex-grow-1">
                    <small className="d-block text-dark">{attachment.filename}</small>
                    <small className="text-muted">{attachment.file_size_formatted}</small>
                  </div>
                </a>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return `${t('today')} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffDays === 1) {
      return `${t('yesterday')} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else {
      return date.toLocaleString([], {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    }
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" />
        <p className="mt-2">{t('loading')}</p>
      </div>
    );
  }

  return (
    <div
      className="chat-box shadow rounded"
      style={{ 
        maxWidth: "800px", 
        margin: "auto", 
        background: "#f0f2f5",
        borderRadius: "10px",
        overflow: "hidden"
      }}
    >
      <div 
        className="bg-color text-white p-3 d-flex align-items-center"
        style={{ borderBottom: "1px solid #ddd" }}
      >
        <h5 className="fw-bold mb-0 flex-grow-1">
          💬 {title} - {customer?.name || t('customer')}
        </h5>
      </div>

      <div
        className="chat-messages p-3 overflow-auto"
        style={{ 
          height: "60vh", 
          background: "url('https://sleekflow.io/static/images/whatsapp-button-generator/whatsApp-bg.jpeg')",
          backgroundSize: "cover"
        }}
      >
        {messages.length === 0 ? (
          <div className="text-center text-muted py-4">
            {t('no_messages_yet')}
          </div>
        ) : (
          messages.map((msg) => {
            const isCustomerMessage = msg.sender.id === parseInt(customerId);
            
            return (
              <div
                key={msg.id}
                className={`mb-3 d-flex ${
                  isCustomerMessage ? "justify-content-start" : "justify-content-end"
                }`}
              >
                <div
                  className={`p-3 rounded position-relative ${
                    isCustomerMessage 
                      ? "bg-white border shadow-sm" 
                      : "bg-color text-white"
                  }`}
                  style={{ 
                    maxWidth: "70%",
                    borderRadius: isCustomerMessage 
                      ? "10px 10px 10px 0" 
                      : "10px 10px 0 10px"
                  }}
                >
                  {/* Message Text */}
                  {msg.message && <div className="message-text mb-2">{msg.message}</div>}
                  
                  {/* Attachments */}
                  {renderMessageAttachments(msg.attachments)}
                  
                  {/* Timestamp and Status */}
                  <div
                    className={`text-small mt-1 d-flex justify-content-end align-items-center ${
                      isCustomerMessage ? "text-muted" : "text-white-50"
                    }`}
                  >
                    <span>{msg.created_at}</span>
                    {!isCustomerMessage && (
                      <span className="ms-2 checkmark-status">
                        {msg.is_read ? '✓✓' : '✓'}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="p-3 bg-white border-top">
        {/* Attachment Preview */}
        {renderAttachmentPreview()}
        
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            sendMessage();
          }}
        >
          <div className="d-flex align-items-center">
            {/* File Attachment Button */}
            <Button 
              variant="outline-secondary" 
              className="rounded-circle me-2"
              onClick={() => fileInputRef.current?.click()}
              title={t('attach_files')}
              style={{ width: '40px', height: '40px' }}
            >
              <FaPaperclip />
            </Button>

            {/* Message Input */}
            <Form.Control
              type="text"
              value={input}
              placeholder={t('write_message_placeholder')}
              onChange={(e) => setInput(e.target.value)}
              disabled={sending}
              className="rounded-pill me-2 flex-grow-1"
              style={{ border: "1px solid #ced4da" }}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
            />

            {/* Send Button */}
            <Button 
              className="rounded-pill btn-color" 
              onClick={sendMessage}
              disabled={sending || (input.trim() === "" && attachments.length === 0)}
              style={{ width: '40px', height: '40px' }}
            >
              {sending ? <Spinner animation="border" size="sm" /> : '➤'}
            </Button>
          </div>

          {/* Hidden File Input */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            multiple
            style={{ display: 'none' }}
            accept="image/*,.pdf,.doc,.docx,.txt,.xls,.xlsx,.zip,.rar"
          />
        </Form>
      </div>
    </div>
  );
}